import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { getFontSize } from "../../../../AppFont";
import {
  InitiativeWithProgressOutput,
  UnitEntity,
} from "../../../../generated/graphql";
import { InitiativeListItem } from "../initiative-list-item/initiative-list-item";
import { ChevronRightIcon } from "@heroicons/react/outline";
import { MstrButton } from "../../../../components/mstr-button/mstr-button";
import { MstrButtonOutline } from "../../../../components/mstr-button-outline/mstr-button-outline";

const INITIAL_VISIBLE_COUNT = 5;

interface ReassignInitiativeData {
  reassignInitiativeToLearner: UnitEntity;
}

const REASSIGN_INITIATIVE = gql`
  mutation ReassignInitiativeToLearner($initiative_id: Int!) {
    reassignInitiativeToLearner(initiative_id: $initiative_id) {
      id
      name
    }
  }
`;

interface InitiativeListCardProps {
  initiatives: InitiativeWithProgressOutput[];
  refetchInitiatives?: () => void | Promise<unknown>;
}

export const InitiativeListCard = ({
  initiatives,
  refetchInitiatives,
}: InitiativeListCardProps) => {
  const [showAll, setShowAll] = useState(false);
  const [initiativeToReassign, setInitiativeToReassign] = useState<
    UnitEntity | undefined
  >(undefined);
  const navigate = useNavigate();

  const [reassignInitiative] = useMutation<ReassignInitiativeData>(
    REASSIGN_INITIATIVE,
    {
      update: () => {
        refetchInitiatives?.();
        setInitiativeToReassign(undefined);
      },
    }
  );

  const visibleInitiatives = showAll
    ? initiatives
    : initiatives.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore = initiatives.length > INITIAL_VISIBLE_COUNT;

  const handleInitiativeClick = (initiativeWithProgress: InitiativeWithProgressOutput) => {
    const initiative = initiativeWithProgress.unit;
    const progress = Number(initiativeWithProgress.progress) ?? 0;
    if (progress >= 100) {
      setInitiativeToReassign(initiative);
    } else {
      const introShown = initiativeWithProgress.seen_intro;
      if (introShown || !initiative.intro) {
        navigate(`/initiative/${initiative.id}`);
      } else {
        navigate(`/initiative-intro/${initiative.id}`);
      }
    }
  };

  const reassignModal =
    initiativeToReassign &&
    createPortal(
      <div
        className="fixed inset-0 flex justify-center items-center"
        style={{
          zIndex: 9999,
          height: "calc(100vh - 64px)",
          left: 0,
          right: 0,
          bottom: 0,
          top: 64,
          background: "#ececec",
        }}
      >
        <div
          className="bg-white rounded-md shadow-lg"
          style={{ padding: 40, maxWidth: "90%" }}
        >
          <div style={{ fontSize: getFontSize() }}>
            Would you like to go through this initiative again?
          </div>
          <br />
          <br />
          <div className="flex gap-3">
            <MstrButton
              width={100}
              onClick={() => {
                reassignInitiative({
                  variables: {
                    initiative_id: +initiativeToReassign.id,
                  },
                });
              }}
              text="Yes"
            />
            <MstrButtonOutline
              width={100}
              onClick={() => setInitiativeToReassign(undefined)}
              text="No"
            />
          </div>
        </div>
      </div>,
      document.body
    );

  console.log("To Reassign: ", initiativeToReassign);

  return (
    <>
      {reassignModal}
      <div className="min-w-0 overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100 pt-3.5 pb-4 pl-5 pr-4">
        <h1 className="font-semibold">My Initiatives ({initiatives.length})</h1>
        {initiatives.length === 0 ? (
          <p className="mt-2 text-gray-500">No initiatives assigned yet.</p>
        ) : (
          <>
            <div className="mt-2 space-y-1 pt-2 -ml-2.5">
              {visibleInitiatives.map((initiativeWithProgress, index) => (
                <InitiativeListItem
                  index={index}
                  key={initiativeWithProgress.unit.id}
                  initiativeWithProgress={initiativeWithProgress}
                  onClick={() => handleInitiativeClick(initiativeWithProgress)}
                />
              ))}
            </div>
            {hasMore && (
              <div
                className="flex justify-end items-center mt-2 pt-2 pr-0 -mr-1"
                style={{ color: "#014ec7" }}
              >
                <button
                  type="button"
                  onClick={() => setShowAll(!showAll)}
                  className="font-semibold"
                  style={{ fontSize: getFontSize() }}
                >
                  {showAll ? "See less" : "See all initiatives"}
                </button>
                <ChevronRightIcon
                  className={`h-4 w-4 ml-1 transition-transform ${showAll ? "rotate-90" : ""
                    }`}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
